import { TypedDocumentNode, gql } from '@apollo/client';
import classnames from 'classnames';
import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Position } from '@sorare/core/src/__generated__/globalTypes';
import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import { glossary } from '@sorare/core/src/lib/glossary';

import CardPlaceholderInfo from 'components/card/CardPlaceholderInfo';
import { PositionIcon } from 'components/lineup/PositionIcon';
import { useIsExtraSlotDisabled } from 'hooks/onboarding/useIsExtraSlotDisabled';
import {
  useComposeTeamOnboardFlow,
  useDisplayOnboardingElements,
} from 'lib/compose-team';

import { CardPlaceholder_so5Leaderboard } from './__generated__/index.graphql';
import gradientGift from './assets/gradient-gift.svg';

const Wrapper = styled(ButtonBase)`
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--c-static-rgb-neutral-100), 0.05);
  border: 2px solid rgba(var(--c-static-rgb-neutral-100), 0.1);
  border-radius: var(--unit);
  width: 100%;
  aspect-ratio: 21 / 34;
  color: var(--c-static-neutral-100);
  cursor: pointer;
  &:hover,
  &:focus {
    background: rgba(var(--c-static-rgb-neutral-100), 0.1);
    border: 2px solid rgba(var(--c-static-rgb-neutral-100), 0.15);
  }
  &.selected {
    background: rgba(var(--c-rgb-brand-600), 0.5);
    border: 2px solid var(--c-brand-600);
  }
  &.isRewardSlot {
    border: 1px solid transparent;
    background:
      linear-gradient(180deg, #191919 0%, #734500 114.17%) padding-box,
      linear-gradient(323deg, #ae5e13 14.3%, #dfad46 85.7%) border-box;
  }
  &.isInBench {
    aspect-ratio: 1;
  }
  &.disabled {
    cursor: initial;
  }
`;

type Props = React.PropsWithChildren<{
  onClick: () => void;
  selected: boolean;
  appearanceName: string;
  shortName: Nullable<string>;
  displayName: Nullable<ReactNode>;
  positions?: Position[];
  isInBench?: boolean;
  so5Leaderboard?: Nullable<CardPlaceholder_so5Leaderboard>;
}>;
export const CardPlaceholder = ({
  onClick,
  selected,
  positions,
  displayName,
  shortName,
  isInBench,
  so5Leaderboard,
  ...rest
}: Props) => {
  const { formatMessage } = useIntl();
  const onboardFlow = useComposeTeamOnboardFlow(so5Leaderboard?.slug);
  const displayOnboardingElements =
    useDisplayOnboardingElements(so5Leaderboard);
  const isExtraSlot = positions?.length === 3;
  const isExtraSlotDisabled = useIsExtraSlotDisabled(so5Leaderboard);

  const isRewardSlot =
    displayOnboardingElements && isExtraSlot && onboardFlow === 'f2p';
  const shouldDisableClick = isExtraSlot && isExtraSlotDisabled;

  const position = positions?.length === 1 ? positions[0] : undefined;
  const icon = position ? (
    <PositionIcon position={position} />
  ) : (
    isRewardSlot && (
      <img src={gradientGift} alt={formatMessage(glossary.reward)} />
    )
  );

  return (
    <Wrapper
      className={classnames({
        selected,
        isRewardSlot,
        isInBench,
        disabled: shouldDisableClick,
      })}
      onClick={shouldDisableClick ? undefined : onClick}
    >
      <CardPlaceholderInfo
        positions={positions}
        {...rest}
        displayName={
          isRewardSlot ? <FormattedMessage {...glossary.reward} /> : displayName
        }
        shortName={isRewardSlot ? undefined : shortName}
        icon={icon}
      />
    </Wrapper>
  );
};

CardPlaceholder.fragments = {
  so5Leaderboard: gql`
    fragment CardPlaceholder_so5Leaderboard on So5Leaderboard {
      slug
      ...useIsExtraSlotDisabled_so5Leaderboard
      ...useDisplayOnboardingElements_so5Leaderboard
    }
    ${useIsExtraSlotDisabled.fragments.so5Leaderboard}
    ${useDisplayOnboardingElements.fragments.so5Leaderboard}
  ` as TypedDocumentNode<CardPlaceholder_so5Leaderboard>,
};
